import { DialogController } from 'aurelia-dialog';
import { autoinject } from 'aurelia-framework';
import { ValidationController, Validator } from 'aurelia-validation';
import { MooringProductionOrderService, MooringService } from 'services/mooring';
import { AddHoursProductionOrderDto, HourProductionOrderDto, MooringProductionTaskType, UpdateHoursProductionOrderDto } from 'models/mooring';
import { AsyncErrorHandler, MnDialog } from 'lib/ui';
import { CustomerService } from 'services/customer-service';
import { SiteService } from 'services/site-service';
import { ToastService } from 'services';
import { PubSub } from 'lib/event/PubSub';
import { MooringProductionOrderEvents } from './events';

export type CreateEditHourItemArgs = {
  productionOrderId: number;
  hourItemId?: number;
  /**
   * Passing this will override the default task options (which is all tasks).
   */
  taskOptions?: MooringProductionTaskType[];
};

@autoinject
export class CreateEditHourItemDialog extends MnDialog<CreateEditHourItemArgs, void> {
  protected loading = true;

  protected productionOrderId?: number;
  protected hourItemId?: number;
  protected taskOptions?: MooringProductionTaskType[];

  protected hourItem?: HourProductionOrderDto;

  constructor(
    dialogControl: DialogController,
    private toastService: ToastService,
    private validationController: ValidationController,
    private mooringProductionOrderService: MooringProductionOrderService,
    private pubsub: PubSub
  ) {
    super(dialogControl);
  }

  activate(args?: CreateEditHourItemArgs) {
    if (!args || !args.productionOrderId) return;

    this.productionOrderId = args.productionOrderId;
    this.hourItemId = args.hourItemId;
    this.taskOptions = args.taskOptions;

    void this.init();
  }

  @AsyncErrorHandler
  protected async init() {
    await this.getHourItem();
  }

  @AsyncErrorHandler
  protected async getHourItem() {
    if (!this.hourItemId || !this.productionOrderId) {
      this.loading = false;
      return;
    }

    const { data } = await this.mooringProductionOrderService.getHour(this.productionOrderId, this.hourItemId);
    this.hourItem = data;
    this.loading = false;
  }

  @AsyncErrorHandler
  async onCreate(model: AddHoursProductionOrderDto) {
    if (!this.productionOrderId || !(await this.validationController.validate()).valid) return;
    await this.mooringProductionOrderService.addHour(this.productionOrderId, model);

    this.toastService.showCreated();

    const payload = { name: MooringProductionOrderEvents.HOURS_ENTITY };
    this.pubsub.publish('entity:created', payload);
    this.pubsub.publish('list-entity:created', payload);

    this.closeWithResult();
  }

  @AsyncErrorHandler
  async onUpdate(model: UpdateHoursProductionOrderDto) {
    if (!this.productionOrderId || !(await this.validationController.validate()).valid) return;

    await this.mooringProductionOrderService.updateHour(this.productionOrderId, this.hourItemId, {
      Comment: model.Comment,
      Date: model.Date,
      Hours: model.Hours,
      Task: model.Task,
      UserId: model.UserId,
    });

    this.toastService.showUpdated();

    const payload = { name: MooringProductionOrderEvents.HOURS_ENTITY };
    this.pubsub.publish('entity:updated', payload);
    this.pubsub.publish('list-entity:updated', payload);

    this.closeWithResult();
  }

  onCancel() {
    void this.cancelDialog();
  }
}
